import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AIDE_CONNEXION,
  AIDE_CONNEXION_PDF,
  AIDE_CONNEXION_PDF_NOM,
  splitEmails,
  type AideBloc,
} from '../content/aideConnexion';

/**
 * Page publique « Aide à la connexion » (/aide/connexion), lisible sans
 * session. Elle n'appelle AUCUNE API : texte tiré de content/aide-connexion.json,
 * le même que le PDF téléchargeable.
 */
export function AideConnexionPage() {
  const c = AIDE_CONNEXION;
  useEffect(() => {
    const avant = document.title;
    document.title = `${c.titre} · Jarvis Support`;
    return () => {
      document.title = avant;
    };
  }, [c.titre]);

  return (
    <div className="aide-page">
      <main className="aide-doc">
        <header className="aide-entete">
          <img src="/dt-logo.png" alt="Digital Team" className="brand-logo" style={{ height: 48 }} />
          <div className="aide-produit">{c.produit}</div>
          <h1>{c.titre}</h1>
          <p className="aide-maj">Mise à jour : {c.miseAJour}</p>
          <div className="aide-actions">
            <a className="btn primary" href={AIDE_CONNEXION_PDF} download={AIDE_CONNEXION_PDF_NOM}>
              Télécharger en PDF
            </a>
            <Link className="btn" to="/login">
              Retour à la connexion
            </Link>
          </div>
        </header>

        <p>{c.intro}</p>

        <nav aria-label="Sommaire" className="aide-sommaire">
          <ul>
            {c.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.titre}</a>
              </li>
            ))}
          </ul>
        </nav>

        {c.sections.map((s) => (
          <section key={s.id} id={s.id} aria-labelledby={`${s.id}-titre`}>
            <h2 id={`${s.id}-titre`}>{s.titre}</h2>
            {s.blocs.map((b, i) => (
              <Bloc key={i} bloc={b} />
            ))}
          </section>
        ))}

        <footer className="aide-pied">
          <Link to="/login">Retour à la connexion</Link>
        </footer>
      </main>
    </div>
  );
}

function Texte({ texte }: { texte: string }) {
  return (
    <>
      {splitEmails(texte).map((m, i) =>
        m.mail ? (
          <a key={i} href={`mailto:${m.texte}`}>
            {m.texte}
          </a>
        ) : (
          <span key={i}>{m.texte}</span>
        ),
      )}
    </>
  );
}

function Bloc({ bloc }: { bloc: AideBloc }): ReactNode {
  switch (bloc.type) {
    case 'h3':
      return <h3>{bloc.texte}</h3>;
    case 'p':
      return (
        <p>
          <Texte texte={bloc.texte} />
        </p>
      );
    case 'note':
      return (
        <p className="aide-note">
          <Texte texte={bloc.texte} />
        </p>
      );
    case 'etapes':
      return (
        <ol className="aide-etapes">
          {bloc.items.map((t, i) => (
            <li key={i}>
              <Texte texte={t} />
            </li>
          ))}
        </ol>
      );
    case 'liste':
      return (
        <ul className="aide-liste">
          {bloc.items.map((t, i) => (
            <li key={i}>
              <Texte texte={t} />
            </li>
          ))}
        </ul>
      );
  }
}
